import * as Api from '@ViewModels';
import { IAccountIntegrations } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Browser } from '../../../../models/Browser';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getIntegrationNameByKey } from '../../../../models/UiUtils';
import { getAutomationPluralNoun } from '../../../../models/utils/automationDisplayConfig';
import {
	useAutomationTemplateAddTriggerMutation,
	useAutomationTemplateSetCancelOnReplyMutation,
} from '../../../../queries';
import { useAutomationEditorContext } from '../../../containers/EditAutomationTemplate/hooks';
import { useAddNewTagLogic } from '../../../hooks/useAddNewTagLogic';
import { baseStyleSheet } from '../../../styles/styles';
import { IAutoCompleteSearchFieldComponent } from '../../autocomplete/AutoCompleteSearchField';
import { SimpleAutoCompleteSearchField } from '../../autocomplete/SimpleAutoCompleteSearchField';
import { Checkbox } from '../../Checkbox';
import { DeprecatedSelect, ISelectOption } from '../../DeprecatedSelect';
import { DangerMessage } from '../../errorMessages/DangerMessage';
import { LoadingSpinner } from '../../LoadingSpinner';
import { AutomationSurveyAutocomplete } from '../../SelectASurvey';
import { AddNewTagFooter } from '../../tags/AddNewTagFooter';
import { TextInput } from '../../TextInput';
import { styleSheet } from './styles';

/**
 * Converts a TimeSpan-like string "DD:HH:MM" to the number of days.
 * @param timeSpan - Format: "DD:HH:MM" where DD is days, HH is hours, MM is minutes
 */
const parseDaysBetweenRepeatedRuns = (timeSpan: string | undefined): number => {
	if (!timeSpan) {
		return 30; // Default value
	}
	const parts = timeSpan.split(':');
	if (parts.length !== 3) {
		return 30; // Default value for invalid format
	}
	const days = parseInt(parts[0], 10);

	if (isNaN(days)) {
		return 30; // Default value for invalid numbers
	}

	return days;
};

const formatDaysBetweenRepeatedRuns = (days: string): string => {
	return `${days}:00:00`;
};

interface IAutomationTriggerSettingsProps<T extends Api.IAutomationTrigger = Api.IAutomationTrigger> {
	automationTemplate?: Api.IAutomationTemplate;
	impersonationContext?: Api.IImpersonationContext;
	onRequestSetTrigger?(trigger: T): Promise<any>;
	trigger?: T;
}

export const AutomationTriggerSettings: React.FC<IAutomationTriggerSettingsProps> = props => {
	const userSession = useUserSession();

	switch (props.trigger?._type) {
		case Api.AutomationTriggerType.Texting: {
			return <AutomationTextingTriggerSettings {...props} />;
		}
		case Api.AutomationTriggerType.Tag: {
			return <AutomationTagTriggerSettings {...props} />;
		}
		case Api.AutomationTriggerType.NewLead: {
			return <AutomationNewLeadTriggerSettings {...props} />;
		}
		case Api.AutomationTriggerType.NewClient:
		case Api.AutomationTriggerType.NewDonationTrigger:
		case Api.AutomationTriggerType.NewDonor:
		case Api.AutomationTriggerType.NewPolicyTrigger:
		case Api.AutomationTriggerType.NewMatterTrigger:
		case Api.AutomationTriggerType.MatterStageChangedTrigger:
		case Api.AutomationTriggerType.MatterStatusChangedTrigger:
		case Api.AutomationTriggerType.ResourceSelector: {
			const resourceSelectorSettings = props.impersonationContext
				? props.impersonationContext.account?.preferences?.resourceSelectorSettings
				: userSession.account?.preferences?.resourceSelectorSettings;
			const typeText = getAutomationPluralNoun(props.trigger, resourceSelectorSettings);

			return <AutomationYoloTriggerSettings props={props} typeText={typeText} />;
		}
		case Api.AutomationTriggerType.EventRegistration: {
			return <AutomationEventRegistrationTriggerSettings {...props} />;
		}
		default: {
			break;
		}
	}
	return null as JSX.Element;
};

interface IAutomationTriggerSettingsCheckboxProps<TTrigger extends Api.IAutomationTrigger = Api.IAutomationTrigger>
	extends IAutomationTriggerSettingsProps<TTrigger> {
	keyInTrigger: keyof TTrigger;
	/** If set to true, the value written to the trigger's key will be the opposite of the <input/> "checked" value */
	trackUnchecked?: boolean;
}

type AutomationTriggerSettingsCheckboxType<T extends Api.IAutomationTrigger = Api.IAutomationTrigger> = React.FC<
	React.PropsWithChildren<IAutomationTriggerSettingsCheckboxProps<T>>
>;

const AutomationTriggerSettingsCheckbox: AutomationTriggerSettingsCheckboxType = props => {
	const { trigger, onRequestSetTrigger, children, keyInTrigger, trackUnchecked, automationTemplate } = props;

	const [checked, setChecked] = React.useState(trackUnchecked ? !trigger?.[keyInTrigger] : !!trigger?.[keyInTrigger]);
	const onCheckChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setChecked(e.target.checked);
			const t: Api.INewLeadAutomationTrigger = {
				...(trigger || {}),
				[keyInTrigger]: trackUnchecked ? !e.target.checked : e.target.checked,
			};
			onRequestSetTrigger?.(t);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[trigger, onRequestSetTrigger, keyInTrigger]
	);
	return (
		<Checkbox
			checked={checked}
			className={css(styleSheet.triggerSettingsCheckbox)}
			disabled={
				!!automationTemplate?.id &&
				!(
					automationTemplate?.status !== Api.TemplateStatus.Archived &&
					automationTemplate?.scope !== Api.TemplateScope.Industry
				)
			}
			id={`automation-trigger-${trigger?._type || ''}-${keyInTrigger}-checkbox`}
			onChange={onCheckChanged}
			type='large'
		>
			<div>
				<div className={css(styleSheet.checkboxText)}>{children}</div>
			</div>
		</Checkbox>
	);
};

const AutomationTriggerSettingsCheckboxes = {
	[Api.AutomationTriggerType.NewLead]:
		AutomationTriggerSettingsCheckbox as AutomationTriggerSettingsCheckboxType<Api.INewLeadAutomationTrigger>,
	default: AutomationTriggerSettingsCheckbox,
} as const;

export const AutomationOnHoldCheckbox: React.FC<React.PropsWithChildren<IAutomationTriggerSettingsProps>> = props => {
	const { trigger, onRequestSetTrigger, children, automationTemplate } = props;

	const [checked, setChecked] = React.useState(
		trigger ? (trigger.putOnHold === undefined || trigger.putOnHold === null ? false : !trigger.putOnHold) : false
	);
	const onCheckChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setChecked(e.target.checked);
			const t: Api.IAutomationTrigger = {
				...(trigger || {}),
				putOnHold: !e.target.checked,
			};
			onRequestSetTrigger?.(t);
		},
		[trigger, onRequestSetTrigger]
	);
	return (
		<Checkbox
			checked={checked}
			className={css(styleSheet.triggerSettingsCheckbox)}
			disabled={
				!!automationTemplate?.id &&
				!(
					automationTemplate?.status !== Api.TemplateStatus.Archived &&
					automationTemplate?.scope !== Api.TemplateScope.Industry
				)
			}
			id='automation-trigger-on-hold-checkbox'
			onChange={onCheckChanged}
			type='large'
		>
			<div>
				<div className={css(styleSheet.checkboxText)}>{children}</div>
			</div>
		</Checkbox>
	);
};

interface IAutomationCancelOnReplyCheckboxProps {
	automationTemplate?: Api.IAutomationTemplate;
	impersonationContext?: Api.IImpersonationContext;
}

export const AutomationCancelOnReplyCheckbox: React.FC<IAutomationCancelOnReplyCheckboxProps> = props => {
	const { automationTemplate, impersonationContext } = props;
	const logger = useEventLogging('AutomationCancelOnReplyCheckbox');
	const errorMessages = useErrorMessages();

	const setCancelOnReplyMutation = useAutomationTemplateSetCancelOnReplyMutation({
		onError: error => {
			logger.logApiError('SetAutomationCancelOnReply-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const [checked, setChecked] = React.useState(
		automationTemplate
			? automationTemplate.cancelOnReply === undefined || automationTemplate.cancelOnReply === null
				? false
				: automationTemplate.cancelOnReply
			: false
	);

	// Sync local state with prop changes (e.g., after cache invalidation and refetch)
	React.useEffect(() => {
		const newChecked = automationTemplate
			? automationTemplate.cancelOnReply === undefined || automationTemplate.cancelOnReply === null
				? false
				: automationTemplate.cancelOnReply
			: false;
		setChecked(newChecked);
	}, [automationTemplate]);

	const onCheckChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (!automationTemplate?.id) {
				return;
			}
			const newChecked = e.target.checked;
			setChecked(newChecked);
			setCancelOnReplyMutation.mutate({
				id: automationTemplate.id,
				cancelOnReply: newChecked,
				impersonationContext,
			});
		},
		[automationTemplate?.id, impersonationContext, setCancelOnReplyMutation]
	);

	return (
		<Checkbox
			checked={checked}
			className={css(styleSheet.cancelOnReplyCheckbox)}
			disabled={
				!automationTemplate?.id ||
				!(
					automationTemplate?.status !== Api.TemplateStatus.Archived &&
					automationTemplate?.scope !== Api.TemplateScope.Industry
				) ||
				setCancelOnReplyMutation.isPending
			}
			id='automation-cancel-on-reply-checkbox'
			onChange={onCheckChanged}
			type='large'
		>
			<div>
				<div className={css(styleSheet.checkboxText, styleSheet.cancelOnReplyCheckboxText)}>
					Cancel the automation if the recipient replies to email or texts.
				</div>
			</div>
		</Checkbox>
	);
};

const AutomationTextingTriggerSettings: React.FC<
	IAutomationTriggerSettingsProps<Api.ITextingCampaignAutomationTrigger>
> = props => {
	const { automationTemplate, trigger, onRequestSetTrigger } = props;
	const logger = useEventLogging('AutomationTriggerCard');
	const errorMessages = useErrorMessages();

	const [checked, setChecked] = React.useState(
		trigger
			? trigger.acknowledgedOnlySendingToOptedIn === undefined || trigger.acknowledgedOnlySendingToOptedIn === null
				? false
				: trigger.acknowledgedOnlySendingToOptedIn
			: false
	);
	const onCheckChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const currentChecked = e.target.checked;
			setChecked(currentChecked);
			if (onRequestSetTrigger) {
				const t: Api.ITextingCampaignAutomationTrigger = {
					...(trigger || {}),
					acknowledgedOnlySendingToOptedIn: currentChecked,
				};
				onRequestSetTrigger(t)?.catch((error: Api.IOperationResultNoValue) => {
					logger.logApiError('SetAutomationTrigger-Error', error);
					errorMessages.pushApiError(error);
					setChecked(!currentChecked);
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[trigger, onRequestSetTrigger]
	);
	return (
		<Checkbox
			checked={checked}
			className={css(styleSheet.triggerSettingsCheckbox)}
			disabled={
				!!automationTemplate?.id &&
				!(
					automationTemplate?.status !== Api.TemplateStatus.Archived &&
					automationTemplate?.scope !== Api.TemplateScope.Industry
				)
			}
			id='automation-trigger-texting-ack-checkbox'
			onChange={onCheckChanged}
			type='large'
		>
			<div>
				<div className={css(styleSheet.checkboxText, styleSheet.textingTriggerCheckboxText)}>
					I agree that the recipients have opted in to receive texts and this is not a marketing campaign.
				</div>
			</div>
		</Checkbox>
	);
};

export const AutomationTagAutocomplete: React.FC<
	IAutomationTriggerSettingsProps & {
		onTagSelected?(tag: string): void;
		className?: string;
		limit?: number;
		initialTag?: string;
	}
> = props => {
	const { automationTemplate, impersonationContext, onTagSelected, limit = 10, initialTag = '' } = props;

	const [searchQuery, setSearchQuery] = React.useState<string>(initialTag);
	const userSession = useUserSession();
	const tags = React.useRef(new Api.TagsViewModel(userSession).impersonate(impersonationContext)).current;

	// #region AutoCompleteSearchField callbacks
	const onCreateAutoCompleteViewModel = React.useCallback(
		(_: Api.ResourceAutoCompleteViewModelType, suggestedVm: Api.ResourceAutoCompleteViewModel) => {
			return tags.isImpersonating ? suggestedVm.impersonate(tags.impersonationContext) : suggestedVm;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const onAutocompleteItemSelectedTagTrigger = React.useCallback(
		(e: { selection?: string | Api.IEntity | Api.IUser; target?: IAutoCompleteSearchFieldComponent }): void => {
			if (e.selection && typeof e.selection === 'string') {
				onTagSelected?.(e.selection);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[automationTemplate, onTagSelected]
	);

	const onInputChange = React.useCallback((e: { sourceEvent?: React.ChangeEvent<HTMLInputElement> }) => {
		if (e.sourceEvent) {
			setSearchQuery(e.sourceEvent.target.value);
		}
	}, []);

	const { handleAddNewTag, onTagSearchFieldRef: onAutoCompleteSearchFieldRef } = useAddNewTagLogic({
		onTagSelected: tagName => {
			setSearchQuery(tagName);
			onAutocompleteItemSelectedTagTrigger({ selection: tagName });
		},
	});
	const onRenderResultsFooter = React.useCallback(
		(query: string, renderedItems: any[], totalCount: number) => {
			if (totalCount === 0 && query) {
				return <AddNewTagFooter searchQuery={query} onAddTag={handleAddNewTag} />;
			}
			return null;
		},
		[handleAddNewTag]
	);
	// #endregion

	return (
		<>
			<SimpleAutoCompleteSearchField
				disabled={
					automationTemplate?.id
						? !(
								automationTemplate.status !== Api.TemplateStatus.Archived &&
								automationTemplate.scope !== Api.TemplateScope.Industry
							)
						: false
				}
				impersonationContext={impersonationContext}
				initialSearchQuery={searchQuery}
				onChange={onInputChange}
				onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
				onItemSelected={onAutocompleteItemSelectedTagTrigger}
				onRenderResultsFooter={onRenderResultsFooter}
				onSearchFieldRef={onAutoCompleteSearchFieldRef}
				pageSize={limit}
				placeholder='Search existing or enter new tag'
				renderNoResultsItem={false}
				resultsLimit={limit}
				style={styleSheet.searchBox}
				type={Api.ResourceAutoCompleteViewModelType.Tag}
			/>
		</>
	);
};

export const AutomationTagTriggerSettings: React.FC<
	IAutomationTriggerSettingsProps & { onTagSelected?(tag: string): void }
> = props => {
	const { impersonationContext, automationTemplate, onRequestSetTrigger } = props;
	const userSession = useUserSession();
	const logger = useEventLogging('AutomationTriggerCard');
	const errorMessages = useErrorMessages();

	const tagTrigger = props.trigger as Api.ITagAutomationTrigger;
	const [allowRepeatedRuns, setAllowRepeatedRuns] = React.useState(!!tagTrigger?.cooldown);
	const [daysBetweenRuns, setDaysBetweenRuns] = React.useState<string>(
		'' + parseDaysBetweenRepeatedRuns(tagTrigger?.cooldown)
	);

	const addTriggerMutation = useAutomationTemplateAddTriggerMutation({
		onError: error => {
			logger.logApiError('AutomationTemplateAddTagTrigger', error);
			errorMessages.pushApiError(error);
		},
	});

	const usersIntegrations = React.useMemo(
		() => (impersonationContext ? impersonationContext.account?.integrations : userSession.account.integrations),
		[impersonationContext, userSession.account.integrations]
	);
	const publicApiEnabled = userSession.account.features?.publicApi?.enabled;
	const enabledIntegrations = React.useMemo(
		() => (usersIntegrations ? Object.keys(usersIntegrations) : []),
		[usersIntegrations]
	);

	const enabledIntegrationNames = React.useMemo(() => {
		const enabledUserIntegrations = enabledIntegrations
			.filter(integration => usersIntegrations[integration as keyof IAccountIntegrations].enabled)
			.map(key => getIntegrationNameByKey(key as keyof IAccountIntegrations, usersIntegrations));

		const publicApiName = 'Zapier';

		return enabledUserIntegrations.join(', ') + (publicApiEnabled ? `, ${publicApiName}` : '');
	}, [enabledIntegrations, usersIntegrations, publicApiEnabled]);

	const canYolo = publicApiEnabled || enabledIntegrationNames.length > 0;

	const putOnHold = props.trigger?.putOnHold;
	const shouldRenderDangerMessage = canYolo && putOnHold !== undefined && putOnHold !== null && !putOnHold;

	const handleAllowRepeatedRunsChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const checked = e.target.checked;
			setAllowRepeatedRuns(checked);
			setDaysBetweenRuns(daysBetweenRuns ?? '30');

			const newTrigger: Api.ITagAutomationTrigger = {
				...(tagTrigger || {}),
				_type: 'TagAutomationTrigger',
				cooldown: checked ? formatDaysBetweenRepeatedRuns(daysBetweenRuns ?? '30') : undefined,
			};
			onRequestSetTrigger?.(newTrigger);
		},
		[tagTrigger, daysBetweenRuns, onRequestSetTrigger]
	);

	const onDayOfInputFocus = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
		const inputElement = e.target as HTMLInputElement;
		if (Browser.isMSEdge()) {
			setTimeout(() => {
				inputElement?.select();
			}, 0);
		} else {
			inputElement.select();
		}
	}, []);

	const handleDaysBetweenRunsChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setDaysBetweenRuns(e.target.value);
	}, []);

	const handleDaysBetweenRunsBlur = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let value = parseInt(e.target.value, 10);
			if (!value || isNaN(value)) {
				value = 30;
			}

			setDaysBetweenRuns('' + value);
			const newTrigger: Api.ITagAutomationTrigger = {
				...(tagTrigger || {}),
				_type: 'TagAutomationTrigger',
				cooldown: formatDaysBetweenRepeatedRuns('' + value),
			};
			onRequestSetTrigger?.(newTrigger);
		},
		[tagTrigger, onRequestSetTrigger]
	);

	return (
		<div>
			<div className={css(styleSheet.triggerSettingsTitle)}>Set a tag as your trigger</div>
			<AutomationTagAutocomplete
				{...props}
				initialTag={(props.trigger as Api.ITagAutomationTrigger)?.tag}
				onTagSelected={tag => {
					const newTrigger: Api.ITagAutomationTrigger = {
						...((props.trigger as Api.ITagAutomationTrigger) || {}),
						_type: 'TagAutomationTrigger',
						tag,
					};
					onRequestSetTrigger?.(newTrigger);

					if (!automationTemplate?.id) {
						return;
					}
					addTriggerMutation.mutate({
						id: automationTemplate.id,
						trigger: newTrigger,
						impersonationContext,
					});
				}}
			/>
			<AutomationOnHoldCheckbox {...props}>
				<div className={css(styleSheet.triggerSettingsCheckboxText)}>
					Automatically approve Automations triggered by this tag
				</div>
			</AutomationOnHoldCheckbox>
			{shouldRenderDangerMessage ? (
				<div className={css(styleSheet.triggerSettingsWarning)}>
					<DangerMessage type='warning'>
						Users will no longer see a card on the dashboard prompting them to approve this automation; it will be
						kicked off automatically.&nbsp;
						<strong className={css(styleSheet.bold)}>
							Only check this box if you feel 100 % confident in the accuracy of your data.
						</strong>
						{` This only affects ${enabledIntegrationNames}.`}
					</DangerMessage>
				</div>
			) : null}
			<Checkbox
				checked={allowRepeatedRuns}
				className={css(styleSheet.triggerSettingsCheckbox)}
				disabled={!automationTemplate?.id}
				id='automation-trigger-tag-repeated-runs-checkbox'
				onChange={handleAllowRepeatedRunsChange}
				type='large'
			>
				<div>
					<div className={css(styleSheet.checkboxText)}>Allow repeated runs</div>
				</div>
			</Checkbox>
			{allowRepeatedRuns && (
				<div className={css(styleSheet.repeatedRunsDaysInput)}>
					<label htmlFor='days-between-runs'>Cooldown time between runs:</label>
					<TextInput
						className={css(baseStyleSheet.removeChildInputSpinButton, styleSheet.daysBetweenTextInput)}
						inputClassName={css(styleSheet.daysBetweenInput)}
						disabled={!automationTemplate?.id}
						inputId={`days-between-runs`}
						min={1}
						onBlur={handleDaysBetweenRunsBlur}
						onChange={handleDaysBetweenRunsChange}
						onFocus={onDayOfInputFocus}
						step={1}
						type='number'
						value={daysBetweenRuns}
					/>
					<label htmlFor='days-between-runs'>{daysBetweenRuns === '1' ? 'day' : 'days'}</label>
				</div>
			)}
		</div>
	);
};

type IAutomationNewLeadTriggerSettingsProps = IAutomationTriggerSettingsProps;

const NewLeadTriggerSettingsCheckbox = AutomationTriggerSettingsCheckboxes[Api.AutomationTriggerType.NewLead];

const AutomationNewLeadTriggerSettings: React.FC<IAutomationNewLeadTriggerSettingsProps> = props => {
	const { automationTemplate, trigger, onRequestSetTrigger, impersonationContext } = props;
	const newLeadTrigger = trigger as Api.INewLeadAutomationTrigger;
	const userSession = useUserSession();
	const logger = useEventLogging('AutomationNewLeadTriggerSettings');
	const { pushApiError: showApiError } = useErrorMessages();
	const [selectOptions, setSelectOptions] = React.useState<ISelectOption<Api.INewLeadAutomationTrigger>[]>([]);
	const [selectedOption, setSelectedOption] = React.useState<ISelectOption<Api.INewLeadAutomationTrigger>>(null);
	const [isLoadingTriggers, setIsLoadingTriggers] = React.useState(false);

	React.useEffect(() => {
		if (selectOptions?.length === 0) {
			const loadTriggers = async () => {
				try {
					setIsLoadingTriggers(true);
					logger.logEvent('TriggersLoad');

					const url = Api.ImpersonationBroker.composeApiUrl({
						urlPath: 'AutomationTemplate/Trigger',
						impersonationContext,
					});

					const response = await userSession.webServiceHelper.callAsync<Api.IAvailableAutomationTrigger[]>(url, 'GET');
					const availableTriggers = response;
					const availableLeadParserTriggers = availableTriggers.filter(
						(x: Api.IAvailableAutomationTrigger) => x.trigger._type === Api.AutomationTriggerType.NewLead
					);

					const options = availableLeadParserTriggers.map<ISelectOption<Api.INewLeadAutomationTrigger>>(
						(x: Api.IAvailableAutomationTrigger) => {
							return {
								dataContext: x.trigger,
								id: x.trigger.id || x.name,
								text: x.name,
							};
						}
					);

					// check to see if the trigger already has a scanner and if it's custom
					const selected =
						!!newLeadTrigger.emailScannerIds?.find((x: Api.EmailScannerId) => x === Api.EmailScannerId.Custom) &&
						newLeadTrigger.customEmailScannerIds?.length > 0
							? // filter to options where id === the id of the first custom scanner/parser
								options.filter(
									(x: ISelectOption<Api.INewLeadAutomationTrigger>) =>
										x.dataContext.customEmailScannerIds?.[0] === newLeadTrigger.customEmailScannerIds[0]
								)
							: // filter to options where id === the id of the first email scanner/parser
								options.filter(
									(x: ISelectOption<Api.INewLeadAutomationTrigger>) =>
										x.dataContext.emailScannerIds?.[0] === newLeadTrigger.emailScannerIds?.[0]
								);

					setSelectOptions(options);
					setSelectedOption(selected?.[0]);
					setIsLoadingTriggers(false);
				} catch (err: unknown) {
					setIsLoadingTriggers(false);
					logger.logApiError('TriggersLoad-Error', err);
					showApiError(err);
				}
			};

			loadTriggers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// #region Select Callbacks
	const onOptionClicked = React.useCallback(
		async (option: ISelectOption<Api.INewLeadAutomationTrigger>, wasSelected: boolean) => {
			if (wasSelected) {
				const checkboxOptions =
					Api.selectKeysOf(trigger as Api.INewLeadAutomationTrigger, ['onlyForNewLeads', 'putOnHold']) || {};
				const t: Api.INewLeadAutomationTrigger = {
					...(trigger || {}),
					...option.dataContext,
					...checkboxOptions, // retain these
				};
				onRequestSetTrigger?.(t);
				setSelectedOption(option);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[automationTemplate, onRequestSetTrigger, trigger]
	);

	const onRenderPlaceholder = React.useCallback(() => {
		if (isLoadingTriggers && selectOptions?.length === 0) {
			return <LoadingSpinner type='tiny' />;
		}
		return <div>Please select...</div>;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoadingTriggers, selectOptions]);
	// #endregion

	return (
		<div>
			<div className={css(styleSheet.triggerSettingsTitle)}>Select a lead source below:</div>
			<DeprecatedSelect
				onOptionClick={onOptionClicked}
				onRenderPlaceholder={onRenderPlaceholder}
				options={selectOptions}
				selectedOption={selectedOption}
				styles={[styleSheet.triggerSelect]}
			/>
			<div>
				<AutomationOnHoldCheckbox {...props}>
					<div className={css(styleSheet.triggerSettingsCheckboxText)}>
						Automatically approve Automations triggered by new leads
					</div>
				</AutomationOnHoldCheckbox>
			</div>
			<div>
				<NewLeadTriggerSettingsCheckbox {...props} keyInTrigger='onlyForNewLeads'>
					<div className={css(styleSheet.triggerSettingsCheckboxText)}>
						Don&apos;t run automation if the lead is already in Levitate
					</div>
				</NewLeadTriggerSettingsCheckbox>
			</div>
			<div>
				<NewLeadTriggerSettingsCheckbox {...props} keyInTrigger='disableObserveSendIntervals'>
					<div className={css(styleSheet.triggerSettingsCheckboxText)}>
						Allow automation to send emails/texts outside of business hours
					</div>
				</NewLeadTriggerSettingsCheckbox>
			</div>
		</div>
	);
};

export const AutomationYoloTriggerSettings = ({
	props,
	typeText,
	extraWarningMessage,
}: {
	props: IAutomationTriggerSettingsProps;
	typeText: string;
	extraWarningMessage?: string;
}) => {
	const putOnHold = props.trigger?.putOnHold;
	const shouldRenderDangerMessage = putOnHold !== undefined && putOnHold !== null && !putOnHold;

	return (
		<>
			<div>
				<AutomationOnHoldCheckbox {...props}>
					<div className={css(styleSheet.triggerSettingsCheckboxText)}>
						{`Automatically approve Automations triggered by ${typeText}.`}
					</div>
				</AutomationOnHoldCheckbox>
			</div>
			<div className={css(styleSheet.triggerSettingsWarning)}>
				{shouldRenderDangerMessage && (
					<DangerMessage type='warning'>
						Users will no longer see a card on the dashboard prompting them to approve this automation; it will be
						kicked off automatically.&nbsp;
						<strong className={css(styleSheet.bold)}>
							Only check this box if you feel 100 % confident in the accuracy of your data.
						</strong>
						{extraWarningMessage ? (
							<>
								<br />
								<br />
								<strong className={css(styleSheet.bold)}>{extraWarningMessage}</strong>
							</>
						) : null}
					</DangerMessage>
				)}
			</div>
		</>
	);
};

export const AutomationEventRegistrationTriggerSettings: React.FC<
	IAutomationTriggerSettingsProps & { onEventSelected?(eventId: string): void }
> = props => {
	const { automationTemplate, impersonationContext } = props;
	const logger = useEventLogging('AutomationTriggerCard');
	const errorMessages = useErrorMessages();
	const automationEditorContext = useAutomationEditorContext();

	const addTriggerMutation = useAutomationTemplateAddTriggerMutation({
		onError: error => {
			logger.logApiError('AutomationTemplateAddEventTrigger', error);
			errorMessages.pushApiError(error);
		},
	});

	return (
		<div>
			<div className={css(styleSheet.triggerSettingsTitle)}>Set an event registration as your trigger</div>
			<AutomationSurveyAutocomplete
				{...props}
				impersonationContext={impersonationContext}
				initialEventName={(props.trigger as Api.IEventRegistrationTrigger)?.eventName}
				onSurveySelected={survey => {
					if (!automationTemplate?.id) {
						return;
					}
					addTriggerMutation.mutate({
						id: automationTemplate.id,
						trigger: {
							_type: 'EventRegistrationTrigger',
							eventId: survey.id,
							eventName: survey.name,
							validResponses: [Api.EventRegistrationResponseStatus.Attending],
						} as Api.IEventRegistrationTrigger,
						impersonationContext,
					});
					props.onEventSelected?.(survey.id);
				}}
				disableInput={!automationEditorContext?.canEdit}
			/>

			<div className={css(styleSheet.eventRegistrationTriggerDisclaimer)}>
				This automation will start when a contact responds with &ldquo;I&apos;m Attending&rdquo; and will be cancelled
				if their response changes.
			</div>
		</div>
	);
};
